import {
  InlineNotification,
  Modal,
  RadioButton,
  RadioButtonGroup,
  TextInput,
  Layer,
} from '@carbon/react';
import { EntityAutocompleteWithCreationModal } from 'components/entity-autocomplete-with-creation-modal';
import { FormRow, FormSection } from 'components/forms';
import { useAtomValue, useSetAtom } from 'jotai';
import { useState } from 'react';
import { EntityResponse } from 'types/api-responses';
import { capacityCreatedAtom, createCapacityAtom } from '../atoms';
import {
  AddCapacityModalProps,
  CurrentEntityDisplayProps,
  EntityRole,
  EntitySelectorProps,
} from './types';

import style from './capacities.module.scss';

const CurrentEntityDisplay = ({
  name,
  label,
  id,
}: CurrentEntityDisplayProps) => (
  <Layer>
    <TextInput id={id} labelText={label} value={name} disabled />
  </Layer>
);

const EntitySelector = ({
  id,
  label,
  selectedEntity,
  onSelect,
}: EntitySelectorProps) => (
  <EntityAutocompleteWithCreationModal
    id={id}
    titleText={label}
    status="new"
    selectedItem={selectedEntity || null}
    hideCreateOption
    onChangeSuccess={(newEntity) => onSelect(newEntity ?? undefined)}
    light
  />
);

export const AddCapacityModal = ({
  onClose,
  open,
  entity,
  getCapacities,
}: AddCapacityModalProps) => {
  const [entityRole, setEntityRole] = useState<EntityRole>('subject');
  const [selectedEntity, setSelectedEntity] = useState<
    EntityResponse | undefined
  >();
  const [predicate, setPredicate] = useState('');
  const [error, setError] = useState<string | undefined>();

  const createCapacity = useSetAtom(createCapacityAtom);
  const createCapacityState = useAtomValue(createCapacityAtom);
  const setCapacityCreated = useSetAtom(capacityCreatedAtom);

  const resetForm = () => {
    setEntityRole('subject');
    setSelectedEntity(undefined);
    setPredicate('');
    setError(undefined);
  };

  const handleEntitySelect = (newEntity: EntityResponse | undefined) => {
    setSelectedEntity(newEntity);
    setError(undefined);
  };

  const handleSubmit = () => {
    if (!selectedEntity) {
      setError('Please select an entity');
      return;
    }
    if (!predicate.trim()) {
      setError('Please enter a capacity');
      return;
    }

    const subjectId = entityRole === 'subject' ? entity.id : selectedEntity.id;
    const objectId = entityRole === 'object' ? entity.id : selectedEntity.id;

    createCapacity({
      url: entity.resources.capacities.href,
      subjectId,
      predicate: predicate.trim(),
      objectId,
      onSuccess: () => {
        getCapacities();
        resetForm();
        setCapacityCreated(true);
        onClose();
      },
      onError: (response) => {
        if (!response) return;
        const errors = response.errors;
        setError(
          errors.base || errors.subject || errors.predicate || errors.object
        );
      },
    });
  };

  return (
    <Modal
      modalHeading="Add Capacity"
      primaryButtonText={createCapacityState.loading ? 'Saving...' : 'Save'}
      primaryButtonDisabled={createCapacityState.loading}
      open={open}
      onRequestSubmit={handleSubmit}
      secondaryButtonText="Cancel"
      onRequestClose={() => {
        resetForm();
        onClose();
      }}
      className={style.addCapacityModal}
    >
      <FormSection>
        <FormRow>
          <RadioButtonGroup
            legendText="This entity is the:"
            name="entity-role"
            valueSelected={entityRole}
            onChange={(value) => {
              setEntityRole(value as EntityRole);
              setError(undefined);
            }}
            orientation="horizontal"
          >
            <RadioButton
              id="role-subject"
              labelText="Who (Entity)"
              value="subject"
            />
            <RadioButton
              id="role-object"
              labelText="Target (Entity)"
              value="object"
            />
          </RadioButtonGroup>
        </FormRow>
        <FormRow>
          <div className={style.capacityRow}>
            {entityRole === 'subject' ? (
              <CurrentEntityDisplay
                id="capacity-subject"
                name={entity.name}
                label="Who (Entity)"
              />
            ) : (
              <EntitySelector
                id="capacity-subject"
                label="Who (Entity)"
                selectedEntity={selectedEntity}
                onSelect={handleEntitySelect}
              />
            )}
            <span className={style.semanticText}>is the</span>
            <Layer>
              <TextInput
                id="capacity-predicate"
                labelText="Capacity"
                placeholder="e.g., Trustee"
                value={predicate}
                onChange={(e) => {
                  setPredicate(e.target.value);
                  setError(undefined);
                }}
              />
            </Layer>
            <span className={style.semanticText}>of</span>
            {entityRole === 'object' ? (
              <CurrentEntityDisplay
                id="capacity-object"
                name={entity.name}
                label="Target (Entity)"
              />
            ) : (
              <EntitySelector
                id="capacity-object"
                label="Target (Entity)"
                selectedEntity={selectedEntity}
                onSelect={handleEntitySelect}
              />
            )}
          </div>
        </FormRow>
        {error && (
          <FormRow>
            <InlineNotification
              lowContrast
              kind="error"
              title=""
              subtitle={error}
              hideCloseButton
            />
          </FormRow>
        )}
      </FormSection>
    </Modal>
  );
};
